/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { Form, useFormik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { FormGroup } from 'react-bootstrap'
import ATInput from '../formComponent/ATInput'
import { FaSearch } from 'react-icons/fa'
import ATDropdown from '../formComponent/ATDropdown'
import './AddressForm.css'
import { useLazyGetGeolocationQuery } from '../../../api/app/GeolocationApiService'
import { Address } from '../../../interface/response/Address'
import { getInitialLocationObject } from '../../../utils/UICommonFunction'

interface AddressFormProps {
  onChangeAddress: (data: Address) => void
  initialValues: Address
  isFromLatLong?: boolean
  isReadOnly?: boolean
  isMandatory?: boolean
}

const AddressForm = forwardRef((props: AddressFormProps, ref) => {
  const {
    onChangeAddress,
    initialValues,
    isFromLatLong = false,
    isReadOnly = false,
    isMandatory = false,
  } = props
  const { t } = useTranslation(['translation'])
  const [getGeoLocationData, geoLocationData] = useLazyGetGeolocationQuery()
  const [locationInfo, setLocationInfo] = useState<any>()
  const [initialLocalCopy, setInitialLocalCopy] = useState<any>()

  useEffect(() => {
    if (JSON.stringify(initialValues) !== JSON.stringify(initialLocalCopy)) {
      setInitialLocalCopy(initialValues)
      setLocationInfo(getInitialLocationObject(initialValues))
    }
  }, [initialValues])

  const getValidationObject = () => {
    return isMandatory
      ? Yup.object({
          street: Yup.string().required(t('form.required')),
          country: Yup.string().required(t('form.required')),
          state: Yup.string().required(t('form.required')),
          city: Yup.string().required(t('form.required')),
          zipCode: Yup.string().required(t('form.required')),
        })
      : Yup.object({})
  }

  const formik = useFormik({
    initialValues: initialValues,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: getValidationObject(),
    onSubmit: (values) => {
      console.log(values)
    },
  })

  useImperativeHandle(ref, () => ({
    getLocationData(lattitude: string, logitude: string) {
      onSearchAction(`${lattitude}, ${logitude}`)
      formik?.setFieldValue('country', '')
      formik?.setFieldValue('state', '')
      formik?.setFieldValue('city', '')
      formik?.setFieldValue('zipCode', '')
      formik?.setFieldValue('street', '')
      onChangeAddress({
        country: '',
        state: '',
        city: '',
        zipCode: '',
        street: '',
        fullAddress: '',
      })
    },
  }))

  useEffect(() => {
    setLocationInfo(geoLocationData?.data)
  }, [geoLocationData?.data])

  const onSearchAction = (searchText: string) => {
    getGeoLocationData(searchText)
  }

  const getFilteredOptions = (key: string, value: string) => {
    switch (key) {
      case 'state':
        return locationInfo?.stateList?.filter((item: any) => {
          return item?.countryCode === value
        })
        break
      case 'city':
        return locationInfo?.cityList?.filter((item: any) => {
          return item?.stateCode === value
        })
        break
      case 'zipCode':
        return locationInfo?.zipCodes?.filter((item: any) => {
          return item?.cityCode === value
        })
        break
      case 'street': {
        const streets = locationInfo?.streetList?.filter((item: any) => {
          return item?.cityCode === value
        })
        const modifiedStreetList = streets?.map((street: any) => {
          return {
            ...street,
            street: street?.streetNumber
              ? street?.streetNumber + ' ' + street?.streetName
              : street?.streetName,
          }
        })
        return modifiedStreetList
      }
      default:
        break
    }
  }

  const countryNameFromCode = (countryCode: string) => {
    return (
      locationInfo?.countryList?.find(
        (country: any) => country?.countryCode === countryCode,
      )?.countryName ?? ''
    )
  }

  const stateNameFromCode = (countryCode: string, stateCode: string) => {
    return (
      getFilteredOptions('state', countryCode)?.find(
        (state: any) => state?.stateCode === stateCode,
      )?.stateName ?? ''
    )
  }

  const cityNameFromCode = (stateCode: string, cityCode: string) => {
    return (
      getFilteredOptions('city', stateCode)?.find(
        (city: any) => city?.cityCode === cityCode,
      )?.cityName ?? ''
    )
  }

  const getFormattedAddress = (values: any) => {
    let finalValue = ''
    if (values?.street) {
      finalValue = `${values?.street}`
    }
    if (values?.city) {
      finalValue = `${finalValue}, ${cityNameFromCode(
        values?.state,
        values?.city,
      )}`
    }
    if (values?.state) {
      finalValue = `${finalValue}, ${stateNameFromCode(
        values?.country,
        values?.state,
      )}`
    }
    if (values?.zipCode) {
      finalValue = `${finalValue}, ${values?.zipCode}`
    }
    if (values?.country) {
      finalValue = `${finalValue}, ${countryNameFromCode(values?.country)}`
    }
    return finalValue
  }

  const updateSelection = (values: any) => {
    onChangeAddress({
      ...values,
      country: countryNameFromCode(values?.country),
      state: stateNameFromCode(values?.country, values?.state),
      city: cityNameFromCode(values?.state, values?.city),
    })
  }

  const getClassName = () => {
    return isFromLatLong ? 'col-4 py-1' : 'col-6 py-1'
  }

  return (
    <div className="p-2">
      <Form onSubmit={formik?.handleSubmit}>
        <div className="row">
          {!isFromLatLong && (
            <FormGroup className="searchField">
              <ATInput
                label={t('common.street')}
                name="street"
                onChangeHandler={(data: any) => {
                  formik?.setFieldValue('street', data?.target?.value)
                  onChangeAddress(formik?.values)
                  onChangeAddress({
                    ...formik?.values,
                    street: data?.target?.value,
                  })
                }}
                disabled={isReadOnly}
                onBlurHandler={formik?.handleBlur}
                value={formik?.values?.street}
                isError={formik?.errors?.street && formik?.touched?.street}
                error={formik?.errors?.street}
                placeholder=""
                isMandatory={isMandatory}
              />
              {formik?.values?.street?.length > 4 && (
                <div className="buttonWrapper">
                  <FaSearch
                    size={16}
                    color="#88a4b6"
                    onClick={() => {
                      if (!isReadOnly) {
                        formik?.setFieldValue('country', '')
                        formik?.setFieldValue('state', '')
                        formik?.setFieldValue('city', '')
                        formik?.setFieldValue('zipCode', '')
                        onSearchAction(formik?.values?.street)
                        onChangeAddress({
                          country: '',
                          state: '',
                          city: '',
                          zipCode: '',
                          street: formik?.values?.street,
                          fullAddress: '',
                        })
                      }
                    }}
                  />
                </div>
              )}
            </FormGroup>
          )}

          <FormGroup className="col-6 py-1">
            <ATDropdown
              options={locationInfo?.countryList || []}
              title={t('common.country')}
              name="country"
              onChange={(country: string) => {
                formik?.setFieldValue('country', country)
                formik?.setFieldValue('state', '')
                formik?.setFieldValue('city', '')
                formik?.setFieldValue('zipCode', '')
                updateSelection({
                  ...formik?.values,
                  country: country,
                  state: '',
                  city: '',
                  zipCode: '',
                })
              }}
              disabled={isReadOnly}
              onBlurHandler={formik?.handleBlur}
              isClearable={true}
              value={formik?.values?.country}
              isError={formik?.errors?.country && formik?.touched?.country}
              error={formik?.errors?.country}
              labelKey="countryName"
              valueKey="countryCode"
              isMandatory={isMandatory}
            />
          </FormGroup>
          <FormGroup className="col-6 py-1">
            <ATDropdown
              options={getFilteredOptions('state', formik?.values?.country)}
              title={t('common.state')}
              name="state"
              onChange={(state: string) => {
                formik?.setFieldValue('state', state)
                formik?.setFieldValue('city', '')
                formik?.setFieldValue('zipCode', '')
                updateSelection({
                  ...formik?.values,
                  state: state,
                  city: '',
                  zipCode: '',
                })
              }}
              disabled={isReadOnly}
              onBlurHandler={formik?.handleBlur}
              isClearable={true}
              value={formik?.values.state}
              isError={formik?.errors?.state && formik?.touched.state}
              error={formik?.errors?.state}
              labelKey="stateName"
              valueKey="stateCode"
              isMandatory={isMandatory}
            />
          </FormGroup>
          <FormGroup className={getClassName()}>
            <ATDropdown
              options={getFilteredOptions('city', formik?.values?.state)}
              title={t('common.city')}
              name="city"
              disabled={isReadOnly}
              onChange={(city: string) => {
                formik?.setFieldValue('city', city)
                formik?.setFieldValue('zipCode', '')
                let streetValue = formik?.values?.street
                if (isFromLatLong) {
                  formik?.setFieldValue('street', '')
                  streetValue = ''
                }
                updateSelection({
                  ...formik?.values,
                  city: city,
                  zipCode: '',
                  street: streetValue,
                })
              }}
              onBlurHandler={formik?.handleBlur}
              isClearable={true}
              value={formik?.values?.city}
              isError={formik?.errors?.city && formik?.touched?.city}
              error={formik?.errors?.city}
              labelKey="cityName"
              valueKey="cityCode"
              isMandatory={isMandatory}
            />
          </FormGroup>
          {isFromLatLong && (
            <FormGroup className={getClassName()}>
              <ATDropdown
                options={getFilteredOptions('street', formik?.values?.city)}
                title={t('common.street')}
                name="street"
                disabled={isReadOnly}
                onChange={(street: string) => {
                  console.log(street)
                  formik?.setFieldValue('street', street)
                  updateSelection({ ...formik?.values, street: street })
                }}
                onBlurHandler={formik?.handleBlur}
                isClearable={true}
                value={formik?.values.street}
                isError={formik?.errors.street && formik?.touched.street}
                error={formik?.errors.street}
                labelKey="streetName"
                valueKey="streetName"
                isMandatory={isMandatory}
              />
            </FormGroup>
          )}
          <FormGroup className={getClassName()}>
            <ATDropdown
              options={getFilteredOptions('zipCode', formik?.values?.city)}
              title={t('common.zip_code')}
              name="zipCode"
              disabled={isReadOnly}
              onChange={(zipCode: string) => {
                formik?.setFieldValue('zipCode', zipCode)
                updateSelection({ ...formik?.values, zipCode: zipCode })
              }}
              onBlurHandler={formik?.handleBlur}
              isClearable={true}
              value={formik?.values.zipCode}
              isError={formik?.errors.zipCode && formik?.touched.zipCode}
              error={formik?.errors.zipCode}
              labelKey="postalCode"
              valueKey="postalCode"
              isMandatory={isMandatory}
            />
          </FormGroup>
          <FormGroup className="col-12 py-1">
            <ATInput
              label={t('common.address')}
              name="fullAddress"
              onChangeHandler={() => {
                console.log('onChange')
              }}
              onBlurHandler={formik?.handleBlur}
              value={getFormattedAddress(formik?.values)}
              isError={
                formik?.errors.fullAddress && formik?.touched.fullAddress
              }
              error={formik?.errors.fullAddress}
              placeholder=""
              disabled={true}
              isMultiLine={true}
              isMandatory={isMandatory}
            />
          </FormGroup>
        </div>
      </Form>
    </div>
  )
})

export default React.memo(AddressForm)
